import { cloneDeep } from "lodash";
import {
  AlertTriangle,
  ArrowLeft,
  Check,
  Copy,
  ExternalLink,
  Pencil,
  Plus,
  Search as SearchIcon,
  Server,
  ShieldOff,
  Trash2,
} from "lucide-react";
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  ConfirmDialog,
  highlightMatchedText,
  Input,
  Label,
  Search,
  Sheet,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@eaigle/ui";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiClient, Deployment } from "../services/api-client";
import "../index.css";
import { LogoutButton } from "../components/LogoutButton";
import { useDeploymentAccess } from "../contexts/useDeploymentAccess";
import { useAPIToken } from "@eaigle/ui";
import { cn } from "@eaigle/utils";

type UrlColumn = {
  label: string;
  field: keyof Deployment;
  getDefault: (deployment: Deployment) => string;
};

const URL_COLUMNS: ReadonlyArray<UrlColumn> = [
  {
    label: "AVAC Backend",
    field: "avacBackendURL",
    getDefault: (deployment) =>
      `https://${deployment.subscriptionName}-avac.azurewebsites.net`,
  },
  {
    label: "CV Backend",
    field: "cvBackendURL",
    getDefault: (deployment) =>
      `https://${deployment.subscriptionName}-cv.azurewebsites.net`,
  },
  {
    label: "Agent Dashboard",
    field: "agentDashboardURL",
    getDefault: () => "",
  },
  {
    label: "AVAC Dashboard",
    field: "avacDashboardURL",
    getDefault: (deployment) => `https://avac.${deployment.subscriptionName}.eaigle.ai`,
  },
  {
    label: "Drivers App",
    field: "driversAppURL",
    getDefault: (deployment) =>
      `https://driverapp.${deployment.subscriptionName}.eaigle.ai`,
  },
  {
    label: "Kiosk App",
    field: "kioskAppURL",
    getDefault: (deployment) => `https://kiosk.${deployment.subscriptionName}.eaigle.ai`,
  },
];

const STICKY_SUBSCRIPTION_CLASS =
  "sticky left-0 z-10 bg-white shadow-[2px_0_6px_-2px_rgba(0,0,0,0.08)]";

const STICKY_SUBSCRIPTION_HEADER_CLASS =
  "sticky left-0 top-0 z-30 bg-white shadow-[2px_0_6px_-2px_rgba(0,0,0,0.08)]";

const STICKY_COLUMN_HEADER_CLASS =
  "sticky top-0 z-20 bg-gray-50 shadow-[0_2px_4px_-2px_rgba(0,0,0,0.06)]";

function createEmptyDeployment(): Deployment {
  return {
    id: "",
    subscriptionName: "",
    avacBackendURL: "",
    cvBackendURL: "",
    agentDashboardURL: "",
    avacDashboardURL: "",
    driversAppURL: "",
    kioskAppURL: "",
  };
}

function getResolvedUrl(deployment: Deployment, column: UrlColumn): string {
  const stored = String(deployment[column.field] ?? "");
  return stored || column.getDefault(deployment);
}

function isNavigableUrl(url: string): boolean {
  return url.startsWith("http://") || url.startsWith("https://");
}

type DeploymentFormProps = {
  draft: Deployment;
  idPrefix: string;
  onFieldChange: (field: keyof Deployment, value: string) => void;
  onUrlFieldChange: (
    field: keyof Deployment,
    value: string,
    defaultValue: string,
  ) => void;
};

function DeploymentForm({
  draft,
  idPrefix,
  onFieldChange,
  onUrlFieldChange,
}: DeploymentFormProps) {
  return (
    <div className="flex flex-col gap-5">
      <div className="space-y-2">
        <Label htmlFor={`${idPrefix}-subscription-name`}>Site</Label>
        <Input
          id={`${idPrefix}-subscription-name`}
          value={draft.subscriptionName}
          placeholder="e.g. my-subscription"
          onChange={(e) => onFieldChange("subscriptionName", e.target.value)}
        />
      </div>

      {URL_COLUMNS.map((column) => {
        const storedValue = String(draft[column.field] ?? "");
        const defaultValue = column.getDefault(draft);
        const displayValue = storedValue || defaultValue;
        const isDefault = !storedValue && defaultValue !== "";
        const inputId = `${idPrefix}-${column.field}`;

        return (
          <div key={column.field} className="space-y-2">
            <Label htmlFor={inputId}>{column.label}</Label>
            <Input
              id={inputId}
              value={displayValue}
              placeholder={column.label}
              className={isDefault ? "text-gray-400" : undefined}
              onChange={(e) => {
                if (defaultValue) {
                  onUrlFieldChange(column.field, e.target.value, defaultValue);
                } else {
                  onFieldChange(column.field, e.target.value);
                }
              }}
            />
          </div>
        );
      })}
    </div>
  );
}

type UrlActionsProps = {
  actionKey: string;
  url: string;
  copiedKey: string | null;
  onCopy: (key: string, text: string) => void;
};

function UrlActions({ actionKey, url, copiedKey, onCopy }: UrlActionsProps) {
  const canOpen = isNavigableUrl(url);
  const isCopied = copiedKey === actionKey;

  if (!url) {
    return <span className="text-xs text-gray-300">—</span>;
  }

  return (
    <div className="flex items-center justify-center gap-0.5">
      {canOpen ?
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0 text-gray-500 hover:text-primary"
              onClick={() => window.open(url, "_blank", "noopener,noreferrer")}
              aria-label={`Open ${url}`}
            >
              <ExternalLink className="h-3.5 w-3.5" />
            </Button>
          </TooltipTrigger>
          <TooltipContent className="max-w-xs break-all">{url}</TooltipContent>
        </Tooltip>
      : null}
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0 text-gray-500 hover:text-primary"
            onClick={() => onCopy(actionKey, url)}
            aria-label="Copy URL"
          >
            {isCopied ?
              <Check className="h-3.5 w-3.5 text-emerald-600" />
            : <Copy className="h-3.5 w-3.5" />}
          </Button>
        </TooltipTrigger>
        <TooltipContent>{isCopied ? "Copied!" : "Copy URL"}</TooltipContent>
      </Tooltip>
    </div>
  );
}

const SettingsPage = () => {
  const navigate = useNavigate();
  const [deployments, setDeployments] = useState<Deployment[]>([]);
  const [deploymentPendingRemoval, setDeploymentPendingRemoval] = useState<
    Deployment | undefined
  >(undefined);
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [sheetMode, setSheetMode] = useState<"add" | "edit">("add");
  const [sheetDraft, setSheetDraft] = useState<Deployment>(createEmptyDeployment);
  const [isSaving, setIsSaving] = useState(false);
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [pageError, setPageError] = useState<"forbidden" | "error" | null>(null);
  const [siteSearch, setSiteSearch] = useState("");
  const copyTimeoutRef = useRef<number | null>(null);
  const { token } = useAPIToken();
  const { hasAdminAccess } = useDeploymentAccess();

  const back = useCallback(() => {
    navigate("/", { replace: true });
  }, [navigate]);

  const refresh = useCallback(async () => {
    if (!token) {
      return;
    }
    try {
      const response = await apiClient.getDeployments(token);
      setDeployments(response);
      setPageError(null);
    } catch (error) {
      const status = (error as { response?: { status?: number } }).response?.status;
      setPageError(status === 403 ? "forbidden" : "error");
    }
  }, [token]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const filteredDeployments = useMemo(() => {
    const query = siteSearch.trim().toLowerCase();
    if (!query) {
      return deployments;
    }
    return deployments.filter((deployment) =>
      deployment.subscriptionName.toLowerCase().includes(query),
    );
  }, [deployments, siteSearch]);

  const openAddSheet = useCallback(() => {
    setSheetMode("add");
    setSheetDraft(createEmptyDeployment());
    setIsSheetOpen(true);
  }, []);

  const openEditSheet = useCallback((deployment: Deployment) => {
    setSheetMode("edit");
    setSheetDraft(cloneDeep(deployment));
    setIsSheetOpen(true);
  }, []);

  const closeSheet = useCallback(() => {
    setIsSheetOpen(false);
    setSheetDraft(createEmptyDeployment());
    setSaveError(null);
  }, []);

  const copyText = useCallback(async (key: string, text: string) => {
    if (!text) {
      return;
    }
    try {
      await navigator.clipboard.writeText(text);
      setCopiedKey(key);
      if (copyTimeoutRef.current !== null) {
        window.clearTimeout(copyTimeoutRef.current);
      }
      copyTimeoutRef.current = window.setTimeout(() => {
        setCopiedKey(null);
        copyTimeoutRef.current = null;
      }, 2000);
    } catch {
      // clipboard unavailable
    }
  }, []);

  useEffect(() => {
    const ref = copyTimeoutRef;
    return () => {
      if (ref.current !== null) window.clearTimeout(ref.current);
    };
  }, []);

  const delActually = useCallback(
    async (deployment: Deployment) => {
      if (!token || !deployment.id) {
        return;
      }
      await apiClient.deleteDeployment(token, deployment.id);
      await refresh();
    },
    [refresh, token],
  );

  const saveDeployment = useCallback(
    async (deployment: Deployment) => {
      if (!token || !hasAdminAccess) {
        return;
      }
      if (deployment.id) {
        await apiClient.putDeployment(token, deployment);
      } else {
        const toCreate = { ...deployment, id: "00000000-0000-0000-0000-000000000000" };
        await apiClient.postDeployment(token, toCreate);
      }
      await refresh();
    },
    [refresh, token, hasAdminAccess],
  );

  const updateSheetField = useCallback((field: keyof Deployment, value: string) => {
    setSheetDraft((prev) => ({
      ...prev,
      [field]: value,
    }));
  }, []);

  const updateSheetUrlField = useCallback(
    (field: keyof Deployment, value: string, defaultValue: string) => {
      setSheetDraft((prev) => ({
        ...prev,
        [field]: value === defaultValue ? "" : value,
      }));
    },
    [],
  );

  const submitSheet = useCallback(async () => {
    if (!sheetDraft.subscriptionName.trim()) {
      return;
    }
    setIsSaving(true);
    setSaveError(null);
    try {
      await saveDeployment({ ...sheetDraft });
      closeSheet();
    } catch {
      setSaveError("Failed to save. Please try again.");
    } finally {
      setIsSaving(false);
    }
  }, [sheetDraft, saveDeployment, closeSheet]);

  const sheetTitle = sheetMode === "add" ? "Add deployment" : "Edit deployment";
  const sheetDescription =
    sheetMode === "add" ?
      "Configure a new subscription and its service endpoints."
    : `Update endpoints for ${sheetDraft.subscriptionName || "this deployment"}.`;

  return (
    <TooltipProvider delayDuration={200}>
      <div className="flex h-full min-h-0 flex-col gap-5 bg-secondary p-6">
        <header className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant="secondary"
                  size="icon"
                  onClick={back}
                  aria-label="Back to deployment versions"
                >
                  <ArrowLeft className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Back to versions</TooltipContent>
            </Tooltip>
            <div>
              <div className="flex items-center gap-2">
                <Server className="h-5 w-5 text-primary" aria-hidden />
                <h1 className="text-xl font-semibold text-gray-900">
                  Deployment settings
                </h1>
              </div>
              <p className="mt-1 text-sm text-gray-600">
                Browse service URLs, open endpoints, or edit a deployment in the side
                panel.
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {hasAdminAccess && (
              <Button
                type="button"
                onClick={openAddSheet}
                leftIcon={<Plus className="h-4 w-4" />}
              >
                Add deployment
              </Button>
            )}
            <LogoutButton />
          </div>
        </header>

        <Card className="flex min-h-0 flex-1 flex-col overflow-hidden border-gray-200/80 shadow-sm">
          <CardHeader className="border-b border-gray-100 px-5 py-4">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <CardTitle className="text-base">Configured deployments</CardTitle>
                <CardDescription>
                  Use the link icon to open a service, copy to grab the URL, or edit to
                  change configuration.
                </CardDescription>
              </div>
              <Search
                value={siteSearch}
                onChange={setSiteSearch}
                placeholder="Search site…"
                aria-label="Search site"
                className="w-full sm:w-72"
                inputClassName="py-3"
                icon={
                  <SearchIcon className="size-4 shrink-0 text-gray-400" aria-hidden />
                }
              />
            </div>
          </CardHeader>

          <CardContent className="flex min-h-0 flex-1 flex-col px-0 py-0">
            {pageError ?
              <div className="flex flex-1 flex-col items-center justify-center gap-3 p-10 text-center">
                {pageError === "forbidden" ?
                  <>
                    <ShieldOff className="h-10 w-10 text-red-400" aria-hidden />
                    <p className="text-sm font-semibold text-gray-800">Access denied</p>
                    <p className="text-sm text-gray-500">
                      You don&apos;t have permission to view this data. Contact your
                      administrator.
                    </p>
                    <LogoutButton variant="labeled" />
                  </>
                : <>
                    <AlertTriangle className="h-10 w-10 text-amber-400" aria-hidden />
                    <p className="text-sm font-semibold text-gray-800">Failed to load</p>
                    <p className="text-sm text-gray-500">
                      Something went wrong while loading data. Try refreshing the page.
                    </p>
                  </>
                }
              </div>
            : <div className="scroll-area min-h-0 flex-1 overflow-auto">
                <div className="min-w-max px-5 pb-4">
                  <table className="w-full border-separate border-spacing-0 text-sm">
                    <thead>
                      <tr>
                        <th
                          className={cn(
                            STICKY_SUBSCRIPTION_HEADER_CLASS,
                            "min-w-50 border-b border-r border-gray-200 px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500",
                          )}
                        >
                          Site
                        </th>
                        {URL_COLUMNS.map((column) => (
                          <th
                            key={column.field}
                            className={cn(
                              STICKY_COLUMN_HEADER_CLASS,
                              "min-w-25 border-b border-gray-200 px-3 py-3 text-center text-xs font-semibold uppercase tracking-wide text-gray-500",
                            )}
                          >
                            {column.label}
                          </th>
                        ))}
                        <th
                          className={cn(
                            STICKY_COLUMN_HEADER_CLASS,
                            "min-w-25 border-b border-gray-200 px-3 py-3 text-center text-xs font-semibold uppercase tracking-wide text-gray-500",
                          )}
                        >
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredDeployments.map((deployment) => {
                        const rowKey = deployment.id || deployment.subscriptionName;

                        return (
                          <tr key={rowKey} className="group hover:bg-gray-50/50">
                            <td
                              className={cn(
                                STICKY_SUBSCRIPTION_CLASS,
                                "border-b border-r border-gray-100 bg-white px-3 py-3 align-middle font-medium text-gray-900 group-hover:bg-gray-50/80",
                              )}
                            >
                              {highlightMatchedText(
                                deployment.subscriptionName,
                                siteSearch,
                              )}
                            </td>
                            {URL_COLUMNS.map((column) => {
                              const url = getResolvedUrl(deployment, column);
                              const actionKey = `${deployment.id}-${column.field}`;

                              return (
                                <td
                                  key={column.field}
                                  className="border-b border-gray-100 px-2 py-2 align-middle"
                                >
                                  <UrlActions
                                    actionKey={actionKey}
                                    url={url}
                                    copiedKey={copiedKey}
                                    onCopy={copyText}
                                  />
                                </td>
                              );
                            })}
                            <td className="border-b border-gray-100 px-2 py-2 align-middle">
                              <div className="flex items-center justify-center gap-0.5">
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="sm"
                                      className="h-8 w-8 p-0 text-gray-500 hover:text-primary"
                                      onClick={() => openEditSheet(deployment)}
                                      aria-label={`Edit ${deployment.subscriptionName}`}
                                    >
                                      <Pencil className="h-3.5 w-3.5" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>Edit deployment</TooltipContent>
                                </Tooltip>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="sm"
                                      className="h-8 w-8 p-0 text-gray-500 hover:text-red-600"
                                      onClick={() =>
                                        setDeploymentPendingRemoval(deployment)
                                      }
                                      aria-label={`Delete ${deployment.subscriptionName}`}
                                    >
                                      <Trash2 className="h-3.5 w-3.5" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>Delete deployment</TooltipContent>
                                </Tooltip>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>

                  {deployments.length === 0 && (
                    <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-gray-200 bg-gray-50/60 px-6 py-12 text-center">
                      <Server className="mb-3 h-8 w-8 text-gray-400" aria-hidden />
                      <p className="text-sm font-medium text-gray-700">
                        No deployments yet
                      </p>
                      <p className="mt-1 text-sm text-gray-500">
                        Add a deployment to configure subscription URLs.
                      </p>
                      {hasAdminAccess && (
                        <Button
                          type="button"
                          className="mt-4"
                          onClick={openAddSheet}
                          leftIcon={<Plus className="h-4 w-4" />}
                        >
                          Add deployment
                        </Button>
                      )}
                    </div>
                  )}

                  {deployments.length > 0 && filteredDeployments.length === 0 && (
                    <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-gray-200 bg-gray-50/60 px-6 py-12 text-center">
                      <SearchIcon className="mb-3 h-8 w-8 text-gray-400" aria-hidden />
                      <p className="text-sm font-medium text-gray-700">
                        No matching sites
                      </p>
                      <p className="mt-1 text-sm text-gray-500">
                        Try a different search term or clear the filter.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            }
          </CardContent>
        </Card>

        <Sheet open={isSheetOpen} onClose={closeSheet} title={sheetTitle} side="right">
          <div className="flex flex-col gap-5 p-6">
            <p className="text-sm text-gray-600">{sheetDescription}</p>

            <DeploymentForm
              draft={sheetDraft}
              idPrefix={sheetMode}
              onFieldChange={updateSheetField}
              onUrlFieldChange={updateSheetUrlField}
            />

            <div className="flex flex-col gap-3 border-t border-gray-100 pt-4">
              {saveError && <p className="text-sm text-red-600">{saveError}</p>}
              <div className="flex justify-end gap-2">
                <Button type="button" variant="secondary" onClick={closeSheet}>
                  Cancel
                </Button>
                <Button
                  type="button"
                  onClick={() => void submitSheet()}
                  disabled={!sheetDraft.subscriptionName.trim() || isSaving}
                  leftIcon={
                    sheetMode === "add" ? <Plus className="h-4 w-4" /> : undefined
                  }
                >
                  {isSaving ?
                    "Saving…"
                  : sheetMode === "add" ?
                    "Add deployment"
                  : "Save changes"}
                </Button>
              </div>
            </div>
          </div>
        </Sheet>

        <ConfirmDialog
          open={deploymentPendingRemoval != undefined}
          onOpenChange={(open) => {
            if (!open) setDeploymentPendingRemoval(undefined);
          }}
          title={"Are you sure?"}
          description={`Are you sure you want to delete ${deploymentPendingRemoval?.subscriptionName}?`}
          confirmLabel={"OK"}
          cancelLabel={"Cancel"}
          variant="danger"
          onConfirm={() => {
            if (deploymentPendingRemoval) {
              void delActually(deploymentPendingRemoval);
            }
          }}
        />
      </div>
    </TooltipProvider>
  );
};

export default SettingsPage;
